<script setup lang="ts">

import { useTitle } from '@vueuse/core';
import BlocksEditor from '~/components/BlocksEditor.vue';
import { useModyApi } from '~/middelware/ModyApi';
import { useUserDataStorage } from '~/store/userdata';
import {useAsyncData} from "#app";
import { Image } from '#components';
import type { FileUploadSelectEvent, FileUploadUploadEvent } from 'primevue/fileupload';

const editMode = ref(false)
const reference = ref<Reference>(new Reference())
const api = useModyApi()
const confirm = useConfirm();
const route = useRoute()
const router = useRouter()
const loaded = ref(false)
const referenceImageLoaded = ref(false)
const userDataStore = useUserDataStorage()
let referenceId = -1


const { data } = await useAsyncData('reference', ()=> loadData())

useSeoMeta({
  title: data.value?.title ? `${data.value.title} – Referenz` : 'Referenz',
  description: data.value?.description,
  ogTitle: data.value?.title,
  ogDescription: data.value?.description,
  ogImage: data.value?.image.id >= 0 ? data.value?.image.url : null,
  twitterCard: 'summary_large_image',
})

const { toHtml } = useEditorJsHtml()
const safeHtml = computed(() => reference.value?.content ? toHtml(reference.value.content) : "")
let editorContent = {}

async function loadData():Promise<any>{
    const referenceId = Number(route.params.id)
    const tempReference = await api.getReference(referenceId)
    return tempReference.toJSON()
}

async function save(){
    editMode.value = false
    reference.value.content = editorContent
    if(referenceId < 0){
        const response = await api.create("references",reference.value.toApiJSON())
        router.push("/references/"+response.data.data.id)
    }
    else{
        await api.update("references/"+referenceId, reference.value.toApiJSON())
    }
}
const confirmRemoval = () => {
  confirm.require({
    message: 'Soll die Referenz wirklich entfernt werden?',
    header: 'ACHTUNG',
    icon: 'pi pi-exclamation-triangle',
    acceptClass: 'p-button-danger',
    rejectLabel: 'Abbrechen',
    acceptLabel: 'Entfernen',
    accept: () => {
      remove()
    }
  });
};
async function remove(){
    await api.remove("references/"+referenceId)
    router.go(-1)
}

watch(route,async ()=>{
    referenceImageLoaded.value = false
    reference.value = Reference.fromJSON(await loadData())
    referenceId = Number(route.params.id)
    editorContent = JSON.parse(JSON.stringify(reference.value.content))
})

onMounted(async()=>{
    referenceId = Number(route.params.id)
    reference.value = Reference.fromJSON(data.value as any)
    editorContent = JSON.parse(JSON.stringify(reference.value.content))
    loaded.value = true;
    if(referenceId < 0) editMode.value = true
})


async function onSelect(event:FileUploadSelectEvent){
  const newFile = (event.files as File[])[0]
  let response = {id:-1, url:""}
  if(newFile){
    response = await api.uploadFile(newFile)
  }
  reference.value.image = response
}
</script>

<template>
  <ConfirmDialog></ConfirmDialog>
  <div v-if="loaded">

    <!-- Edit Mode: Admin Controls oben -->
    <div v-if="editMode" class="bg-zinc-800 border-b border-zinc-700 px-6 py-4">
      <div class="max-w-3xl mx-auto flex flex-col gap-4">
        <InputText v-model="reference.title" placeholder="Titel" class="text-xl font-bold w-full" />
        <InputText v-model="reference.description" placeholder="Kurzbeschreibung" class="w-full" />
        <div class="flex items-center gap-4">
          <Image v-if="reference.image.url" :src="reference.image.url" height="60px" class="rounded-lg" />
          <FileUpload
            customUpload
            mode="basic"
            :choose-label="reference.image.url ? 'Bild ändern' : 'Bild auswählen'"
            class="p-button-outlined"
            @select="onSelect"
          />
        </div>
      </div>
    </div>

    <!-- Hero: mit Bild (View Mode) -->
    <div
      v-if="!editMode && reference.image.url"
      class="reference-hero relative h-[50vh] min-h-[320px] overflow-hidden"
      :aria-busy="!referenceImageLoaded"
    >
      <Skeleton v-if="!referenceImageLoaded" class="reference-hero__skeleton" aria-hidden="true" />
      <img
        :src="reference.image.url"
        :alt="reference.title"
        class="reference-hero__image absolute inset-0 w-full h-full object-cover"
        :class="{ 'reference-hero__image--loaded': referenceImageLoaded }"
        @load="referenceImageLoaded = true"
        @error="referenceImageLoaded = true"
      />
      <div class="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
      <div class="absolute bottom-0 left-0 right-0 px-6 pb-10 text-center">
        <h1 class="text-4xl sm:text-5xl font-bold text-white mb-3 drop-shadow-lg">{{ reference.title }}</h1>
        <p v-if="reference.description" class="text-stone-200 text-lg max-w-2xl mx-auto drop-shadow">{{ reference.description }}</p>
      </div>
    </div>

    <!-- Hero: ohne Bild (View Mode) -->
    <div v-if="!editMode && !reference.image.url" class="public-quiet-hero bg-zinc-900 py-20 px-6 text-center">
      <h1 class="text-4xl sm:text-5xl font-bold text-white mb-4">{{ reference.title }}</h1>
      <p v-if="reference.description" class="text-stone-300 text-xl max-w-2xl mx-auto">{{ reference.description }}</p>
    </div>

    <!-- Breadcrumb -->
    <div class="bg-zinc-800 px-6 py-4 border-b border-zinc-700">
      <div class="max-w-3xl mx-auto flex items-center justify-between">
        <NuxtLink to="/references" class="flex items-center gap-2 text-stone-400 hover:text-white transition-colors text-sm">
          <i class="pi pi-arrow-left text-xs"></i>
          Alle Referenzen
        </NuxtLink>
        <div v-if="userDataStore.loggedIn" class="flex gap-2">
          <Button v-if="!editMode" size="small" severity="secondary" @click="editMode = true">
            <i class="pi pi-pencil mr-1"></i> Bearbeiten
          </Button>
          <Button v-if="editMode" size="small" @click="save()">
            <i class="pi pi-check mr-1"></i> Speichern
          </Button>
          <Button v-if="editMode && referenceId != -1" size="small" severity="danger" @click="confirmRemoval()">
            <i class="pi pi-trash mr-1"></i> Entfernen
          </Button>
        </div>
      </div>
    </div>

    <!-- Content -->
    <div class="bg-zinc-900 py-12 px-6">
      <div class="max-w-3xl mx-auto">
        <LazyBlocksEditor class="blocksEditor" v-if="reference.content && editMode" v-model="editorContent" />
        <div v-if="!editMode && reference.content" v-html="safeHtml" class="blocksEditor" />
      </div>
    </div>

    <!-- CTA -->
    <div class="public-quiet-section bg-zinc-800 py-16 px-6 text-center">
      <h2 class="text-3xl font-bold text-white mb-4">Auch so ein Event planen?</h2>
      <p class="text-stone-300 text-lg mb-8 max-w-xl mx-auto">
        Wir freuen uns auf deine Anfrage – kostenlos & unverbindlich. Wir melden uns schnell mit einem passenden Angebot.
      </p>
      <div class="flex flex-col sm:flex-row gap-4 justify-center">
        <Button as="router-link" to="/shop/request" class="px-8">Angebot anfordern</Button>
        <Button as="router-link" to="/references" severity="secondary" class="px-8">Mehr Referenzen</Button>
      </div>
    </div>

  </div>
</template>

<style lang="scss">
.reference-hero{
  background: $public-surface-raised;
}

.reference-hero__skeleton{
  position: absolute;
  inset: 0;
  width: 100% !important;
  height: 100% !important;
  border-radius: 0 !important;
}

.reference-hero__image{
  opacity: 0;
  transition: opacity 220ms $public-ease;
}

.reference-hero__image--loaded{
  opacity: 1;
}

.blocksEditor {
  h3 {
    margin-top: 28px;
    margin-bottom: 8px;
    font-size: 20px;
    font-weight: 700;
    color: white;
  }
  p {
    box-sizing: content-box;
    margin-bottom: 14px;
    color: #d6d3d1; /* stone-300 */
    line-height: 1.75;
    font-size: 17px;
  }
  ol, ul {
    list-style-type: disc;
    padding-left: 24px;
    margin-bottom: 14px;
    color: #d6d3d1;
    li {
      margin-bottom: 6px;
      line-height: 1.6;
    }
  }
  ol {
    list-style-type: decimal;
  }
  img {
    @apply rounded-xl shadow-lg my-6 w-full;
    max-height: 70vh;
    object-fit: cover;
  }
  .offerButton {
    background-color: $accentColor;
    border-radius: 3px;
    margin: auto;
    margin-top: 10px;
    color: black;
    padding: 2px 6px;
    display: block;
  }
}
</style>
